import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../config/supabase';
import { useAuth } from '../context/AuthContext';
import { markActivityAsSeen } from '../services/adminActivityService';
import Layout from '../components/Layout';
import { formatDate, formatVal, fileName, currencySymbol } from '../utils/formatters';
import './ClientProductDetail.css';

const BUCKET = 'client-portal-uploads';

const ClientProductDetail = () => {
  const { clientId, productId } = useParams();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [clientName, setClientName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [downloadingPath, setDownloadingPath] = useState(null);

  const loadProduct = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: fetchError } = await supabase
        .from('client_products')
        .select('*, client_profiles!inner(company_name)')
        .eq('id', productId)
        .eq('client_profile_id', clientId)
        .single();

      if (fetchError) {
        setError(fetchError.message || 'Product not found');
        setProduct(null);
        setLoading(false);
        return;
      }

      setProduct(data);
      setClientName(data.client_profiles?.company_name || 'Client');

      if (user?.id) {
        markActivityAsSeen(user.id, 'client_product', productId);
      }
    } catch (err) {
      setError(err.message || 'Failed to load product');
      setProduct(null);
    } finally {
      setLoading(false);
    }
  }, [clientId, productId, user?.id]);

  useEffect(() => {
    if (!clientId || !productId) return;
    loadProduct();
  }, [clientId, productId, loadProduct]);

  const handleDownload = async (path) => {
    if (!path) return;
    setDownloadingPath(path);
    try {
      const { data, error: urlError } = await supabase.storage
        .from(BUCKET)
        .createSignedUrl(path, 60);
      if (urlError) {
        console.error('Download error:', urlError);
        setDownloadingPath(null);
        return;
      }
      if (data?.signedUrl) {
        window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error('Download error:', err);
    }
    setDownloadingPath(null);
  };

  if (loading) {
    return (
      <Layout>
        <div className="client-product-detail">
          <p className="client-product-detail-loading">Loading...</p>
        </div>
      </Layout>
    );
  }

  if (error || !product) {
    return (
      <Layout>
        <div className="client-product-detail">
          <div className="client-product-detail-error">{error || 'Product not found'}</div>
          <Link to={`/clients/${clientId}`} className="client-product-detail-back">← Back to client</Link>
        </div>
      </Layout>
    );
  }

  const itemTypeLabel = { bottle: 'Bottle', can: 'Can', item: 'Item' }[product.item_type] || product.item_type || '—';

  return (
    <Layout>
      <div className="client-product-detail">
        <div className="client-product-detail-header">
          <Link to={`/clients/${clientId}`} className="client-product-detail-back">← Back to {clientName}</Link>
          <h1>{product.product_name || 'Product'}</h1>
          <p className="client-product-detail-meta">
            {clientName} · SKU {formatVal(product.sku_number)}
            {' · '}
            <span className={`client-product-detail-status client-product-detail-status-${(product.status || '').toLowerCase()}`}>
              {(product.status || 'draft').toUpperCase()}
            </span>
          </p>
        </div>

        <section className="client-product-detail-section">
          <h2>Product / SKU</h2>
          <dl className="client-product-detail-dl">
            <dt>SKU number</dt>
            <dd>{formatVal(product.sku_number)}</dd>
            <dt>Product name</dt>
            <dd>{formatVal(product.product_name)}</dd>
            <dt>Product description</dt>
            <dd>{formatVal(product.product_description)}</dd>
            <dt>Volume (ml)</dt>
            <dd>{formatVal(product.volume)}</dd>
            <dt>Alcohol ABV (%)</dt>
            <dd>{formatVal(product.alcohol_abv)}</dd>
            <dt>Commodity / HS code</dt>
            <dd>{formatVal(product.commodity_code)}</dd>
            <dt>Country of manufacture</dt>
            <dd>{formatVal(product.country_of_manufacture)}</dd>
            <dt>Shelf life</dt>
            <dd>{formatVal(product.shelf_life)}</dd>
            <dt>Item type</dt>
            <dd>{itemTypeLabel}</dd>
          </dl>
        </section>

        <section className="client-product-detail-section">
          <h2>Packaging & logistics</h2>
          <dl className="client-product-detail-dl">
            <dt>Pack size</dt>
            <dd>{formatVal(product.pack_size)}</dd>
            <dt>Quantity per case (units)</dt>
            <dd>{formatVal(product.quantity_per_case)}</dd>
            <dt>Item material</dt>
            <dd>{formatVal(product.bottle_material)}</dd>
            <dt>Cases per pallet</dt>
            <dd>{formatVal(product.cases_per_pallet)}</dd>
            <dt>Net case weight (kg)</dt>
            <dd>{formatVal(product.net_case_weight)}</dd>
            <dt>Gross case weight (kg)</dt>
            <dd>{formatVal(product.gross_case_weight)}</dd>
            <dt>Case dimensions (mm)</dt>
            <dd>
              {[product.case_height_mm, product.case_length_mm, product.case_width_mm].every((n) => n != null && n !== '') 
                ? `${product.case_height_mm} × ${product.case_length_mm} × ${product.case_width_mm}` 
                : '—'}
            </dd>
          </dl>
        </section>

        <section className="client-product-detail-section">
          <h2>Pricing</h2>
          <dl className="client-product-detail-dl">
            <dt>RRP per bottle ({currencySymbol(product.currency_rrp_bottle || product.currency)})</dt>
            <dd>{formatVal(product.rrp_per_bottle_gbp)}</dd>
            <dt>RRP per case B2B ({currencySymbol(product.currency_rrp_case || product.currency)})</dt>
            <dd>{formatVal(product.rrp_per_case_b2b_gbp)}</dd>
            <dt>Cost price per bottle ({currencySymbol(product.currency_cost_bottle || product.currency)})</dt>
            <dd>{formatVal(product.cost_price_per_bottle_gbp)}</dd>
            <dt>Cost price per case ({currencySymbol(product.currency_cost_case || product.currency)})</dt>
            <dd>{formatVal(product.cost_price_per_case_gbp)}</dd>
          </dl>
        </section>

        <section className="client-product-detail-section">
          <h2>Uploaded assets</h2>
          <dl className="client-product-detail-dl client-product-detail-assets">
            <dt>Label image (front)</dt>
            <dd>
              {product.label_image_front_path ? (
                <>
                  <span className="client-product-detail-filename">{fileName(product.label_image_front_path)}</span>
                  <button type="button" className="client-product-detail-download" onClick={() => handleDownload(product.label_image_front_path)} disabled={downloadingPath === product.label_image_front_path}>
                    {downloadingPath === product.label_image_front_path ? '…' : 'Download'}
                  </button>
                </>
              ) : '—'}
            </dd>
            <dt>Label image (back)</dt>
            <dd>
              {product.label_image_back_path ? (
                <>
                  <span className="client-product-detail-filename">{fileName(product.label_image_back_path)}</span>
                  <button type="button" className="client-product-detail-download" onClick={() => handleDownload(product.label_image_back_path)} disabled={downloadingPath === product.label_image_back_path}>
                    {downloadingPath === product.label_image_back_path ? '…' : 'Download'}
                  </button>
                </>
              ) : '—'}
            </dd>
            <dt>EAN / barcode close-up</dt>
            <dd>
              {product.ean_barcode_closeup_path ? (
                <>
                  <span className="client-product-detail-filename">{fileName(product.ean_barcode_closeup_path)}</span>
                  <button type="button" className="client-product-detail-download" onClick={() => handleDownload(product.ean_barcode_closeup_path)} disabled={downloadingPath === product.ean_barcode_closeup_path}>
                    {downloadingPath === product.ean_barcode_closeup_path ? '…' : 'Download'}
                  </button>
                </>
              ) : '—'}
            </dd>
            <dt>Brand guidelines</dt>
            <dd>
              {product.brand_guidelines_path ? (
                <>
                  <span className="client-product-detail-filename">{fileName(product.brand_guidelines_path)}</span>
                  <button type="button" className="client-product-detail-download" onClick={() => handleDownload(product.brand_guidelines_path)} disabled={downloadingPath === product.brand_guidelines_path}>
                    {downloadingPath === product.brand_guidelines_path ? '…' : 'Download'}
                  </button>
                </>
              ) : '—'}
            </dd>
            <dt>Brand book</dt>
            <dd>
              {product.brand_book_path ? (
                <>
                  <span className="client-product-detail-filename">{fileName(product.brand_book_path)}</span>
                  <button type="button" className="client-product-detail-download" onClick={() => handleDownload(product.brand_book_path)} disabled={downloadingPath === product.brand_book_path}>
                    {downloadingPath === product.brand_book_path ? '…' : 'Download'}
                  </button>
                </>
              ) : '—'}
            </dd>
          </dl>
        </section>

        <p className="client-product-detail-updated">
          Last updated {formatDate(product.updated_at)} · Created {formatDate(product.created_at)}
        </p>
      </div>
    </Layout>
  );
};

export default ClientProductDetail;
